// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  AppShell,
  Avatar,
  Box,
  Burger,
  Button,
  Combobox,
  Group,
  Kbd,
  Loader,
  Menu,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  useCombobox,
  useMantineColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { formatHumanName, getReferenceString } from '@medplum/core';
import type { Bundle, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  IconBell,
  IconChevronUp,
  IconClipboardCheck,
  IconMicrophone,
  IconMicrophoneOff,
  IconMoon,
  IconPhone,
  IconPhoneOff,
  IconPictureInPicture,
  IconScreenShare,
  IconScreenShareOff,
  IconSearch,
  IconSun,
  IconVideo,
  IconVideoOff,
  IconWand,
} from '@tabler/icons-react';
import { useState, useCallback, useEffect } from 'react';
import type { JSX, ReactNode, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCareComm } from '../../contexts/CareCommContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useThemeColor } from '../../contexts/ThemeColorContext';
import { DoseSpotIcon } from '../DoseSpotIcon';
import { hasDoseSpotIdentifier } from '../utils';
import { AuthenticatedAvatar } from '../AuthenticatedAvatar';
import { getPatientPhotoUrl, getPatientInitials } from '../../utils/imageUtils';
import { calculateAgeYears, formatBirthDate } from '../../utils/birthDate';
import { Sidebar } from './Sidebar';
import classes from './AppLayout.module.css';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const { isOpen, toggleOpen, mode, isExpanded } = useSidebar();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  // Note: aiButtonColor is applied via CSS custom property --carepoint-ai-button-color
  useThemeColor(); // Initialize theme color context
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const { applyOrganizationFilter } = useOrganization();
  const membership = medplum.getProjectMembership();
  const hasDoseSpot = hasDoseSpotIdentifier(membership);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // CareComm context for minimized call footer
  const {
    activeCall,
    endCall,
    exitPiPMode,
    enterPiPMode,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
  } = useCareComm();

  // Check if call is minimized (show footer)
  const showCallFooter = activeCall?.displayMode === 'minimized';

  // Add/remove body class when call footer is visible (for global CSS access)
  useEffect(() => {
    if (showCallFooter) {
      document.body.classList.add('has-call-footer');
    } else {
      document.body.classList.remove('has-call-footer');
    }
    return () => {
      document.body.classList.remove('has-call-footer');
    };
  }, [showCallFooter]);

  // Format call duration
  const formatCallDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  }, []);

  // Helper to detect search type and build appropriate query
  const detectSearchType = useCallback((query: string): { type: string; value: string } => {
    const trimmed = query.trim();

    // Check for age (just a number, typically 0-120)
    if (/^\d{1,3}$/.test(trimmed)) {
      const age = parseInt(trimmed, 10);
      if (age >= 0 && age <= 120) {
        return { type: 'age', value: trimmed };
      }
    }

    // Check for phone number (digits, dashes, parentheses, spaces, plus sign)
    if (/^[\d\s\-\(\)\+\.]{7,}$/.test(trimmed)) {
      const digitsOnly = trimmed.replace(/\D/g, '');
      if (digitsOnly.length >= 7) {
        return { type: 'phone', value: trimmed };
      }
    }

    // Check for email (contains @ or looks like a domain/email part)
    if (trimmed.includes('@') || /^[a-zA-Z0-9._-]+\.(com|org|net|edu|gov|io|in|co|us|uk|ca|au|de|fr|es|it|jp|cn|br|mx|ru|info|biz)$/i.test(trimmed)) {
      return { type: 'email', value: trimmed };
    }

    // Check for SSN pattern (XXX-XX-XXXX or 9 digits)
    if (/^\d{3}-?\d{2}-?\d{4}$/.test(trimmed) || /^\d{9}$/.test(trimmed)) {
      return { type: 'ssn', value: trimmed };
    }

    // Check for MRN/ID (alphanumeric, typically starts with letters or has specific format like PT0025, #PT0025)
    if (/^#?[A-Za-z]{1,3}\d+$/.test(trimmed) || /^\d{6,}$/.test(trimmed)) {
      return { type: 'identifier', value: trimmed.replace(/^#/, '') };
    }

    // Default to universal search (search across all fields)
    return { type: 'universal', value: trimmed };
  }, []);

  const calculateAge = useCallback((birthDate: string | undefined): number | null => {
    return calculateAgeYears(birthDate);
  }, []);

  // Format gender for display
  const formatGender = useCallback((gender: string | undefined): string => {
    if (!gender) return 'Unknown';
    switch (gender.toLowerCase()) {
      case 'male': return 'Male';
      case 'female': return 'Female';
      case 'other': return 'Other';
      case 'unknown': return 'Unknown';
      default: return gender.charAt(0).toUpperCase() + gender.slice(1);
    }
  }, []);

  const formatBirthday = useCallback((birthDate: string | undefined): string => {
    return formatBirthDate(birthDate) ?? 'DOB Unknown';
  }, []);

  // Search patients when debounced search query changes
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setPatients([]);
      return;
    }

    const searchPatients = async (): Promise<void> => {
      setLoading(true);
      try {
        const { type, value } = detectSearchType(debouncedSearch);
        let allResults: Patient[] = [];

        // Build search params based on search type
        const baseParams = applyOrganizationFilter({
          _count: '50',
          _sort: '-_lastUpdated',
        });

        if (type === 'phone') {
          // Search by phone - fetch patients and filter client-side for partial matching
          const digitsOnly = value.replace(/\D/g, '');
          const bundle = await medplum.search('Patient', { ...baseParams }) as Bundle<Patient>;
          const patients = bundle.entry?.map((e) => e.resource as Patient) || [];
          // Filter patients whose phone contains the search digits
          allResults = patients.filter((p) => {
            const phones = p.telecom?.filter((t) => t.system === 'phone') || [];
            return phones.some((phone) => {
              const phoneDigits = phone.value?.replace(/\D/g, '') || '';
              return phoneDigits.includes(digitsOnly);
            });
          });
        } else if (type === 'email') {
          // Search by email - fetch patients and filter client-side for partial matching
          const searchValue = value.toLowerCase();
          const bundle = await medplum.search('Patient', { ...baseParams }) as Bundle<Patient>;
          const patients = bundle.entry?.map((e) => e.resource as Patient) || [];
          // Filter patients whose email contains the search value
          allResults = patients.filter((p) => {
            const emails = p.telecom?.filter((t) => t.system === 'email') || [];
            return emails.some((email) => email.value?.toLowerCase().includes(searchValue));
          });
        } else if (type === 'ssn' || type === 'identifier') {
          // Search by identifier - fetch patients and filter client-side for partial matching
          // Remove # prefix if present and search for the value
          const searchValue = value.toLowerCase().replace(/^#/, '');
          const bundle = await medplum.search('Patient', { ...baseParams }) as Bundle<Patient>;
          const patients = bundle.entry?.map((e) => e.resource as Patient) || [];
          // Filter patients whose identifier contains the search value
          allResults = patients.filter((p) => {
            return p.identifier?.some((id) => {
              const idValue = id.value?.toLowerCase().replace(/^#/, '') || '';
              return idValue.includes(searchValue);
            });
          });
        } else if (type === 'age') {
          // Search by age - find patients born in the year that would make them this age
          const age = parseInt(value, 10);
          const today = new Date();
          const birthYear = today.getFullYear() - age;

          // Search for patients born in that year (approximate age match)
          const bundle = await medplum.search('Patient', {
            ...baseParams,
            birthdate: `${birthYear}`,
          }) as Bundle<Patient>;
          allResults = bundle.entry?.map((e) => e.resource as Patient) || [];
        } else if (type === 'universal') {
          // Universal search - search across all fields (name, email, phone, identifier)
          const searchValue = value.toLowerCase();
          const bundle = await medplum.search('Patient', { ...baseParams }) as Bundle<Patient>;
          const patients = bundle.entry?.map((e) => e.resource as Patient) || [];

          // Filter patients that match any field
          allResults = patients.filter((p) => {
            // Check name
            const nameMatch = p.name?.some((n) => {
              const fullName = [n.prefix?.join(' '), n.given?.join(' '), n.family, n.suffix?.join(' ')]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return fullName.includes(searchValue);
            });
            if (nameMatch) return true;

            // Check email
            const emailMatch = p.telecom
              ?.filter((t) => t.system === 'email')
              .some((t) => t.value?.toLowerCase().includes(searchValue));
            if (emailMatch) return true;

            // Check phone
            const phoneMatch = p.telecom
              ?.filter((t) => t.system === 'phone')
              .some((t) => t.value?.toLowerCase().includes(searchValue));
            if (phoneMatch) return true;

            // Check identifier
            const idMatch = p.identifier?.some((id) => id.value?.toLowerCase().includes(searchValue));
            if (idMatch) return true;

            return false;
          });
        }

        // If no results with specific search, try universal search as fallback
        if (allResults.length === 0 && type !== 'universal') {
          const searchValue = value.toLowerCase().replace(/^#/, '');
          const bundle = await medplum.search('Patient', { ...baseParams }) as Bundle<Patient>;
          const patients = bundle.entry?.map((e) => e.resource as Patient) || [];

          // Filter patients that match any field (universal fallback)
          allResults = patients.filter((p) => {
            // Check name
            const nameMatch = p.name?.some((n) => {
              const fullName = [n.prefix?.join(' '), n.given?.join(' '), n.family, n.suffix?.join(' ')]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return fullName.includes(searchValue);
            });
            if (nameMatch) return true;

            // Check email
            const emailMatch = p.telecom
              ?.filter((t) => t.system === 'email')
              .some((t) => t.value?.toLowerCase().includes(searchValue));
            if (emailMatch) return true;

            // Check phone
            const phoneMatch = p.telecom
              ?.filter((t) => t.system === 'phone')
              .some((t) => t.value?.toLowerCase().includes(searchValue));
            if (phoneMatch) return true;

            // Check identifier (MRN, etc.)
            const idMatch = p.identifier?.some((id) => {
              const idValue = id.value?.toLowerCase().replace(/^#/, '') || '';
              return idValue.includes(searchValue);
            });
            if (idMatch) return true;

            return false;
          });
        }

        // Limit to 8 results for display
        setPatients(allResults.slice(0, 8));
        if (allResults.length > 0) {
          combobox.openDropdown();
        }
      } catch (err) {
        console.error('Error searching patients:', err);
        setPatients([]);
      } finally {
        setLoading(false);
      }
    };

    searchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, medplum, applyOrganizationFilter, detectSearchType]);

  const handlePatientSelect = useCallback(
    (patientId: string) => {
      setSearchQuery('');
      setPatients([]);
      combobox.closeDropdown();
      navigate(`/Patient/${patientId}`);
    },
    [navigate, combobox]
  );

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      // Navigate to patient search with the query
      navigate(`/Patient?name=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setPatients([]);
      combobox.closeDropdown();
    }
  }, [searchQuery, navigate, combobox]);

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // Calculate navbar width based on mode and expanded state
  // On mobile, use full width when opened
  const navbarWidth = { base: '100%', sm: mode === 'hover' && !isExpanded ? 68 : 240 };

  // Get patient name for minimized call
  const callPatientName = activeCall?.patient?.name?.[0]
    ? formatHumanName(activeCall.patient.name[0])
    : 'Patient';

  const isVideoCall = activeCall?.type === 'video-call';
  const CallIcon = isVideoCall ? IconVideo : IconPhone;

  return (
    <AppShell
      header={{ height: 48 }}
      navbar={{
        width: navbarWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !isOpen, desktop: !isOpen },
      }}
      footer={showCallFooter ? { height: 56 } : undefined}
      padding="md"
    >
      <AppShell.Header className={classes.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          {/* Left side: Logo, Burger */}
          <Group gap="md" className={classes.headerLeft}>
            <img src="/img/app-logo-wide.png" alt="Logo" className={classes.logo} />
            <Burger opened={isOpen} onClick={toggleOpen} size="sm" className={classes.burger} />
          </Group>

          {/* Center: Search */}
          <Box className={classes.headerCenter}>
            <Combobox
              store={combobox}
              onOptionSubmit={(val) => handlePatientSelect(val)}
            >
              <Combobox.Target>
                <TextInput
                  placeholder={t('header.searchPlaceholder')}
                  leftSection={<IconSearch size={14} />}
                  rightSection={
                    loading ? (
                      <Loader size={12} color="white" />
                    ) : (
                      <Kbd size="xs" className={classes.kbd}>
                        ⏎
                      </Kbd>
                    )
                  }
                  size="xs"
                  className={classes.searchInput}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.currentTarget.value);
                    if (e.currentTarget.value.trim()) {
                      combobox.openDropdown();
                    } else {
                      combobox.closeDropdown();
                    }
                  }}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => {
                    if (patients.length > 0) {
                      combobox.openDropdown();
                    }
                  }}
                  onBlur={() => combobox.closeDropdown()}
                />
              </Combobox.Target>

              <Combobox.Dropdown className={classes.searchDropdown}>
                <Combobox.Options>
                  {patients.length > 0 ? (
                    patients.map((patient) => {
                      const age = calculateAge(patient.birthDate);
                      const ageDisplay = age !== null ? `${age}y` : '';
                      return (
                        <Combobox.Option
                          key={patient.id}
                          value={patient.id || ''}
                          className={classes.searchOption}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <AuthenticatedAvatar
                              size={36}
                              radius="xl"
                              src={getPatientPhotoUrl(patient)}
                            >
                              {getPatientInitials(patient)}
                            </AuthenticatedAvatar>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <Text size="sm" fw={500} truncate>
                                {formatHumanName(patient.name?.[0])}
                              </Text>
                              <Text size="xs" c="dimmed" truncate>
                                {formatBirthday(patient.birthDate)} • {formatGender(patient.gender)}{ageDisplay && ` • ${ageDisplay}`}
                              </Text>
                            </div>
                          </Group>
                        </Combobox.Option>
                      );
                    })
                  ) : (
                    <Combobox.Empty>
                      {loading ? t('header.searching') : t('header.noPatientsFound')}
                    </Combobox.Empty>
                  )}
                  {patients.length > 0 && (
                    <Combobox.Footer className={classes.searchFooter}>
                      <Text size="xs" c="dimmed">
                        {t('header.pressEnterForResults')}
                      </Text>
                    </Combobox.Footer>
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </Box>

          {/* Right side: Actions */}
          <Group gap="sm" className={classes.headerRight}>
            <Button
              leftSection={<IconWand size={14} />}
              size="xs"
              className={classes.aiButton}
              onClick={() => navigate('/spaces')}
            >
              <span className={classes.aiButtonText}>{t('header.nulouAi')}</span>
            </Button>

            <ActionIcon variant="subtle" size="md" className={`${classes.headerIcon} ${classes.hideOnMobile}`}>
              <IconBell size={18} />
            </ActionIcon>

            {profile && (
              <ActionIcon
                variant="subtle"
                size="md"
                className={classes.headerIcon}
                onClick={() =>
                  navigate(
                    `/Task?owner=${getReferenceString(profile)}&status:not=completed&_fields=subject,code,description,status,_lastUpdated`
                  )
                }
                aria-label={t('header.tasks')}
              >
                <IconClipboardCheck size={18} />
              </ActionIcon>
            )}

            {hasDoseSpot && <DoseSpotIcon />}

            <ActionIcon
              variant="subtle"
              size="md"
              className={classes.headerIcon}
              onClick={() => toggleColorScheme()}
            >
              {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>

            <Avatar size={30} radius="xl" className={classes.userAvatar}>
              {profile?.name?.[0]?.given?.[0]?.charAt(0) || 'U'}
            </Avatar>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <Sidebar />
      </AppShell.Navbar>

      <AppShell.Main className={`${classes.main} ${showCallFooter ? classes.mainWithFooter : ''}`}>
        <Box className={classes.content}>{children}</Box>
      </AppShell.Main>

      {/* Minimized Call Footer - True footer that pushes content up */}
      {showCallFooter && activeCall && (
        <AppShell.Footer className={classes.callFooter}>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            {/* Left: Call info */}
            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <Box style={{ position: 'relative' }}>
                <AuthenticatedAvatar
                  size={32}
                  radius="xl"
                  src={getPatientPhotoUrl(activeCall.patient)}
                >
                  {getPatientInitials(activeCall.patient)}
                </AuthenticatedAvatar>
                <Box className={classes.callTypeBadge}>
                  <CallIcon size={10} />
                </Box>
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} c="white" truncate>
                  {callPatientName}
                </Text>
                <Group gap={4}>
                  <Box className={classes.liveDot} />
                  <Text size="xs" c="dimmed">
                    {activeCall.state === 'connected'
                      ? formatCallDuration(activeCall.duration)
                      : 'Connecting...'}
                  </Text>
                </Group>
              </Box>
            </Group>

            {/* Center: Quick controls */}
            <Group gap="xs" className={classes.callControls}>
              <Tooltip label={activeCall.isMuted ? 'Unmute' : 'Mute'} position="top">
                <ActionIcon
                  variant={activeCall.isMuted ? 'filled' : 'subtle'}
                  color={activeCall.isMuted ? 'red' : 'gray'}
                  size="lg"
                  radius="xl"
                  onClick={toggleMute}
                >
                  {activeCall.isMuted ? (
                    <IconMicrophoneOff size={18} />
                  ) : (
                    <IconMicrophone size={18} />
                  )}
                </ActionIcon>
              </Tooltip>

              {isVideoCall && (
                <Tooltip label={activeCall.isCameraOff ? 'Camera on' : 'Camera off'} position="top">
                  <ActionIcon
                    variant={activeCall.isCameraOff ? 'filled' : 'subtle'}
                    color={activeCall.isCameraOff ? 'red' : 'gray'}
                    size="lg"
                    radius="xl"
                    onClick={toggleCamera}
                    className={classes.hideOnMobile}
                  >
                    {activeCall.isCameraOff ? (
                      <IconVideoOff size={18} />
                    ) : (
                      <IconVideo size={18} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}

              {isVideoCall && (
                <Tooltip label={activeCall.isScreenSharing ? 'Stop sharing' : 'Share screen'} position="top">
                  <ActionIcon
                    variant={activeCall.isScreenSharing ? 'filled' : 'subtle'}
                    color={activeCall.isScreenSharing ? 'blue' : 'gray'}
                    size="lg"
                    radius="xl"
                    onClick={toggleScreenShare}
                    className={classes.hideOnMobile}
                  >
                    {activeCall.isScreenSharing ? (
                      <IconScreenShareOff size={18} />
                    ) : (
                      <IconScreenShare size={18} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}

              <Tooltip label="End call" position="top">
                <ActionIcon
                  variant="filled"
                  color="red"
                  size="lg"
                  radius="xl"
                  onClick={endCall}
                >
                  <IconPhoneOff size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {/* Right: Restore options */}
            <Group gap="xs">
              <Tooltip label="Picture-in-Picture" position="top">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  radius="xl"
                  onClick={enterPiPMode}
                >
                  <IconPictureInPicture size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Full screen" position="top">
                <ActionIcon
                  variant="light"
                  color="green"
                  size="md"
                  radius="xl"
                  onClick={exitPiPMode}
                >
                  <IconChevronUp size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </AppShell.Footer>
      )}
    </AppShell>
  );
}
